import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import { createAppTheme } from '../theme';

/**
 * UI preferences (colour mode + density) with persistence.
 *
 * The MUI theme is rebuilt whenever a preference changes. MUI v5 has no
 * `colorSchemes` API, so this is the supported way to do light/dark in v5.
 */
const STORAGE_KEY = 'ui:preferences:v1';
const DEFAULT_PREFERENCES = { mode: 'light', density: 'comfortable' };

const UiThemeContext = createContext(null);

const readPreferences = () => {
  if (typeof window === 'undefined') return DEFAULT_PREFERENCES;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFERENCES;
    const parsed = JSON.parse(raw);
    return {
      mode: parsed?.mode === 'dark' ? 'dark' : 'light',
      density: parsed?.density === 'compact' ? 'compact' : 'comfortable'
    };
  } catch (_) {
    return DEFAULT_PREFERENCES;
  }
};

export function UiThemeProvider({ children }) {
  const [preferences, setPreferences] = useState(readPreferences);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    } catch (_) {
      // Ignore storage access issues; the theme still applies for this session.
    }

    // Keep the browser chrome (mobile address bar) in step with the theme.
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) {
      meta.setAttribute('content', preferences.mode === 'dark' ? '#0f141a' : '#0a56a5');
    }
  }, [preferences]);

  const theme = useMemo(
    () => createAppTheme(preferences),
    [preferences]
  );

  const value = useMemo(() => ({
    mode: preferences.mode,
    density: preferences.density,
    isDark: preferences.mode === 'dark',
    isCompact: preferences.density === 'compact',
    toggleMode: () => setPreferences((prev) => ({ ...prev, mode: prev.mode === 'dark' ? 'light' : 'dark' })),
    toggleDensity: () => setPreferences((prev) => ({ ...prev, density: prev.density === 'compact' ? 'comfortable' : 'compact' })),
    setMode: (mode) => setPreferences((prev) => ({ ...prev, mode: mode === 'dark' ? 'dark' : 'light' })),
    setDensity: (density) => setPreferences((prev) => ({ ...prev, density: density === 'compact' ? 'compact' : 'comfortable' }))
  }), [preferences.mode, preferences.density]);

  return (
    <UiThemeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </UiThemeContext.Provider>
  );
}

export function useUiTheme() {
  const context = useContext(UiThemeContext);
  if (!context) {
    throw new Error('useUiTheme must be used within UiThemeProvider');
  }
  return context;
}

export default UiThemeProvider;
